/**
 * OData Query Builder
 * Utility for building OData query strings for filtering, sorting, and paging.
 */

export type FilterOperator =
  | 'eq'      // Equal
  | 'ne'      // Not equal
  | 'gt'      // Greater than
  | 'ge'      // Greater than or equal
  | 'lt'      // Less than
  | 'le'      // Less than or equal
  | 'contains'
  | 'startswith'
  | 'endswith'

export interface FilterCondition {
  field: string
  operator: FilterOperator
  value: string | number | boolean | Date | null
}

export interface SortCondition {
  field: string
  direction: 'asc' | 'desc'
}

export interface ODataQueryParams {
  filter?: FilterCondition[]
  orderBy?: SortCondition[]
  skip?: number
  top?: number
  count?: boolean
  select?: string[]
  expand?: string[]
  search?: string
}

/**
 * Escapes a string value for use in OData query.
 */
function escapeODataValue(value: string): string {
  return value.replace(/'/g, "''")
}

/**
 * Checks if a string is a valid GUID format.
 * GUIDs follow the pattern: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx
 */
function isGuid(value: string): boolean {
  const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
  return guidRegex.test(value)
}

/**
 * Formats a value for OData query.
 */
function formatValue(value: string | number | boolean | Date | null): string {
  if (value === null) {
    return 'null'
  }
  if (typeof value === 'string') {
    // GUIDs should not be wrapped in quotes in OData
    if (isGuid(value)) {
      return value
    }
    return `'${escapeODataValue(value)}'`
  }
  if (typeof value === 'boolean') {
    return value.toString()
  }
  if (typeof value === 'number') {
    return value.toString()
  }
  if (value instanceof Date) {
    return value.toISOString()
  }
  return String(value)
}

/**
 * Builds a single filter expression.
 */
function buildFilterExpression(condition: FilterCondition): string {
  const { field, operator, value } = condition
  const formattedValue = formatValue(value)

  switch (operator) {
    case 'contains':
      return `contains(${field}, ${formattedValue})`
    case 'startswith':
      return `startswith(${field}, ${formattedValue})`
    case 'endswith':
      return `endswith(${field}, ${formattedValue})`
    default:
      return `${field} ${operator} ${formattedValue}`
  }
}

/**
 * Builds an OData query string from parameters.
 */
export function buildODataQuery(params: ODataQueryParams): string {
  const queryParts: string[] = []

  // $filter
  if (params.filter && params.filter.length > 0) {
    const filterExpressions = params.filter.map(buildFilterExpression)
    queryParts.push(`$filter=${filterExpressions.join(' and ')}`)
  }

  // $orderby
  if (params.orderBy && params.orderBy.length > 0) {
    const orderByExpressions = params.orderBy.map(
      (sort) => `${sort.field} ${sort.direction}`
    )
    queryParts.push(`$orderby=${orderByExpressions.join(',')}`)
  }

  // $skip
  if (params.skip !== undefined && params.skip > 0) {
    queryParts.push(`$skip=${params.skip}`)
  }

  // $top
  if (params.top !== undefined) {
    queryParts.push(`$top=${params.top}`)
  }

  // $count
  if (params.count) {
    queryParts.push('$count=true')
  }

  // $select
  if (params.select && params.select.length > 0) {
    queryParts.push(`$select=${params.select.join(',')}`)
  }

  // $expand
  if (params.expand && params.expand.length > 0) {
    queryParts.push(`$expand=${params.expand.join(',')}`)
  }

  // $search
  if (params.search) {
    queryParts.push(`$search="${escapeODataValue(params.search)}"`)
  }

  return queryParts.length > 0 ? `?${queryParts.join('&')}` : ''
}

/**
 * Interface for OData response with count
 */
export interface ODataResponse<T> {
  value: T[]
  '@odata.count'?: number
}

/**
 * Interface for table state management
 */
export interface TableState {
  page: number
  pageSize: number
  sortField?: string
  sortDirection?: 'asc' | 'desc'
  filters: Record<string, FilterCondition>
  search?: string
}

/**
 * Converts table state to OData query params.
 */
export function tableStateToODataParams(state: TableState): ODataQueryParams {
  const params: ODataQueryParams = {
    skip: (state.page - 1) * state.pageSize,
    top: state.pageSize,
    count: true,
  }

  if (state.sortField) {
    params.orderBy = [{
      field: state.sortField,
      direction: state.sortDirection || 'asc'
    }]
  }

  const filterConditions = Object.values(state.filters).filter(
    (f) => f.value !== null && f.value !== undefined && f.value !== ''
  )
  if (filterConditions.length > 0) {
    params.filter = filterConditions
  }

  if (state.search) {
    params.search = state.search
  }

  return params
}

/**
 * Default table state
 */
export const defaultTableState: TableState = {
  page: 1,
  pageSize: 20,
  filters: {},
}
